<template>
  <div class="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div class="flex items-center justify-between">
      <h2 class="text-base font-semibold text-slate-900">{{ title }}</h2>
      <button
        v-if="showAdd"
        @click="$emit('add')"
        class="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
      >
        新增分类
      </button>
    </div>
    <p v-if="description" class="mt-2 text-xs text-slate-500">{{ description }}</p>

    <!-- 搜索 -->
    <div v-if="showSearch" class="mt-3">
      <div class="relative">
        <input
          v-model="filterText"
          type="text"
          placeholder="搜索分类..."
          class="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
        <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    </div>

    <!-- 全部选项 -->
    <div class="mt-4 space-y-1">
      <div
        @click="$emit('select', null)"
        :class="[
          'flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition',
          selectedId === null
            ? 'bg-primary-50 font-medium text-primary-700'
            : 'text-slate-600 hover:bg-slate-50'
        ]"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        <span>全部分类</span>
      </div>

      <!-- 加载状态 -->
      <template v-if="loading">
        <div v-for="n in 4" :key="n" class="animate-pulse rounded-lg bg-slate-50 px-3 py-3">
          <div class="h-3.5 w-20 rounded bg-slate-200"></div>
        </div>
      </template>

      <!-- 树节点 -->
      <template v-else>
        <KbCategoryTreeNode
          v-for="node in filteredTree"
          :key="node.id"
          :node="node"
          :depth="0"
          :selected-id="selectedId"
          :expanded-ids="expandedIds"
          :show-actions="showActions"
          @select="(id) => $emit('select', id)"
          @toggle="toggleExpand"
          @edit="(node) => $emit('edit', node)"
          @delete="(node) => $emit('delete', node)"
          @add-child="(node) => $emit('add-child', node)"
        />
      </template>

      <div v-if="!loading && !filteredTree.length" class="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
        暂无分类数据
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import KbCategoryTreeNode from './KbCategoryTreeNode.vue'

const props = defineProps({
  tree: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  selectedId: { type: [Number, String], default: null },
  title: { type: String, default: '分类管理' },
  description: { type: String, default: '' },
  showAdd: { type: Boolean, default: true },
  showActions: { type: Boolean, default: true },
  showSearch: { type: Boolean, default: true }
})

defineEmits(['add', 'edit', 'delete', 'select', 'add-child'])

const filterText = ref('')
const expandedIds = ref(new Set())

const toggleExpand = (id) => {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id)
  } else {
    expandedIds.value.add(id)
  }
}

const filterTree = (nodes, text) => {
  if (!text) return nodes
  const lower = text.toLowerCase()
  return nodes.reduce((acc, node) => {
    const nameMatch = node.name?.toLowerCase().includes(lower)
    const filteredChildren = node.children ? filterTree(node.children, text) : []
    if (nameMatch || filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren.length ? filteredChildren : node.children })
      if (filteredChildren.length) {
        expandedIds.value.add(node.id)
      }
    }
    return acc
  }, [])
}

const filteredTree = computed(() => filterTree(props.tree, filterText.value))
</script>
